import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { CustomizationLayoutsService, UserLayoutDto } from '@myarvato/core/customization';
import { SkeletonModule } from 'primeng/skeleton';
import { TransportViewDto } from '@myarvato/modules/transport/models';
import { SavedViewComponent } from './components/saved-view/saved-view.component';
import { DataView } from 'primeng/dataview';
import { Router } from '@angular/router';
import { UserTrackingService } from '@myarvato/core/services';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Message } from 'primeng/message';
import { CustomizationTranslationService } from '@myarvato/shared/services';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'transport-saved-views',
  imports: [SkeletonModule, SavedViewComponent, DataView, Message, TranslatePipe, ButtonModule],
  templateUrl: './saved-views.component.html',
  styleUrl: './saved-views.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SavedViewsComponent implements OnInit {
  // Injections
  private readonly customizationLayoutsService = inject(CustomizationLayoutsService<TransportViewDto>);
  private readonly router = inject(Router);
  private readonly userTrackingService = inject(UserTrackingService);
  private readonly translateService = inject(TranslateService);
  private readonly customizationTranslationService = inject(CustomizationTranslationService);

  // Fields
  protected readonly context = 'transport';
  protected readonly trackingCategory = 'Transport - Overview - Saved Views';

  // Change detection
  protected readonly state = signal<{
    loading: boolean;
    error: unknown | undefined;
    data: UserLayoutDto<TransportViewDto>[] | undefined;
  }>({
    loading: true,
    error: undefined,
    data: undefined,
  });

  protected readonly isOnError = computed(() => this.state().error !== undefined);
  protected readonly isSuccess = computed(() => this.state().data !== undefined && !this.isOnError() && !this.isEmpty());
  protected readonly isLoading = computed(() => this.state().loading);
  protected readonly isEmpty = computed(() => this.state().data !== undefined && (this.state()?.data?.length ?? 0) < 1);
  protected readonly data = computed(() => this.state().data ?? []);

  protected readonly errorMessage = computed(() => {
    const exception = this.state().error as { error: { errorCode: string } };
    const errorCode = exception?.error?.errorCode || 'G0400';
    const errorTranslationTitle = this.customizationTranslationService.getTranslation(`errors.${errorCode}.title`, 'errors.G0400.title');
    const errorTranslationDescription = this.customizationTranslationService.getTranslation(
      `errors.${errorCode}.description`,
      'errors.G0400.description',
    );

    return {
      title: errorTranslationTitle,
      description: errorTranslationDescription,
    };
  });

  public ngOnInit(): void {
    this.customizationLayoutsService.getLayouts(this.context).subscribe({
      next: (data) => {
        const myLayouts = data?.filter((c) => c.isMy);
        this.state.set({ loading: false, error: undefined, data: myLayouts });
      },
      error: (error) => {
        console.error(error);
        this.state.set({ loading: false, error: error, data: undefined });
      },
    });
  }

  protected goToTrackingList() {
    this.userTrackingService.trackEvent(this.trackingCategory, 'Go to Tracking List clicked');

    this.router.navigate(['transport', 'list']).then();
  }
}
